import type { ReviseHistoryItem } from '@formency/shared';
import type { BadgeVariant } from '@/interfaces/learner/ui';
import { formatDurationMs } from '@/shared/utils/formatDuration';

const HIGH_THRESHOLD = 80;
const MID_THRESHOLD = 50;

export interface DateGroup {
  key: string;
  label: string;
  items: ReviseHistoryItem[];
}

export function dateKey(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatGroupDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const key = dateKey(dateStr);
  const todayKey = dateKey(now.toISOString());
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = dateKey(yesterday.toISOString());

  if (key === todayKey) return 'Today';
  if (key === yesterdayKey) return 'Yesterday';

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return date.toLocaleDateString(undefined, { weekday: 'long' });

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: now.getFullYear() !== date.getFullYear() ? 'numeric' : undefined,
  });
}

export function groupByDate(items: ReviseHistoryItem[]): DateGroup[] {
  const groups: DateGroup[] = [];
  let current: DateGroup | null = null;

  for (const item of items) {
    const key = dateKey(item.startedAt);
    if (!current || current.key !== key) {
      current = { key, label: formatGroupDate(item.startedAt), items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }

  return groups;
}

export function getRevisionStatusLabel(status: string): string {
  return status === 'abandoned' ? 'Abandoned' : 'Completed';
}

export function getRevisionBadgeVariant(scorePct: number | null, status: string): BadgeVariant {
  if (status === 'abandoned') return 'surface';
  const pct = scorePct ?? 0;
  if (pct >= HIGH_THRESHOLD) return 'success';
  if (pct >= MID_THRESHOLD) return 'warning';
  return 'error';
}

export function getRevisionScoreColor(scorePct: number | null, status: string): string {
  if (status === 'abandoned') return 'var(--color-text-muted)';
  const pct = scorePct ?? 0;
  if (pct >= HIGH_THRESHOLD) return 'var(--color-success-text)';
  if (pct >= MID_THRESHOLD) return 'var(--color-warning-text)';
  return 'var(--color-error-text)';
}

export function getRevisionAccentColor(scorePct: number | null, status: string): string {
  if (status === 'abandoned') return 'var(--color-border)';
  const pct = scorePct ?? 0;
  if (pct >= HIGH_THRESHOLD) return 'var(--color-success)';
  if (pct >= MID_THRESHOLD) return 'var(--color-warning)';
  return 'var(--color-error)';
}

export function formatRevisionScore(scorePct: number | null, status: string): string {
  return status === 'abandoned' ? 'Abandoned' : `${Math.round(scorePct ?? 0)}%`;
}

export function formatRevisionQuestionSummary(
  questionsCorrect: number | null,
  questionsTotal: number | null,
  status: string
): string {
  const correct = questionsCorrect ?? 0;
  const total = questionsTotal ?? 0;
  return status === 'abandoned' ? `${correct}/${total} answered` : `${correct}/${total} correct`;
}

export function formatRevisionDurationMinutes(durationMinutes: number | null): string {
  if (durationMinutes == null || durationMinutes <= 0) return '—';
  return formatDurationMs(durationMinutes * 60_000);
}

export function summariseRevisionHistory(items: ReviseHistoryItem[]) {
  const completed = items.filter((item) => item.status !== 'abandoned');
  const completedWithScores = completed.filter((item) => item.scorePct != null);
  const totalMinutes = completed.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0);
  const averageAccuracy =
    completedWithScores.length > 0
      ? Math.round(
          completedWithScores.reduce((sum, item) => sum + (item.scorePct ?? 0), 0) /
            completedWithScores.length
        )
      : null;
  const bestScore =
    completedWithScores.length > 0
      ? Math.round(Math.max(...completedWithScores.map((item) => item.scorePct ?? 0)))
      : null;

  return {
    totalSessions: items.length,
    completedSessions: completed.length,
    averageAccuracy,
    bestScore,
    totalDurationLabel: totalMinutes > 0 ? formatDurationMs(totalMinutes * 60_000) : '—',
  };
}
